import { useCallback } from 'react';
import { mutate } from 'swr';
import { unstable_serialize } from 'swr/infinite';
import { api } from '../lib/api';
import { setCategoryFragmentFromCategory } from '../lib/category-fragment-cache';
import { useRegion } from './useRegion';
import { getCategoryProductsKey } from '../utils/ssr-to-swr';
import { CATALOG_PRODUCTS_PER_PAGE } from '../lib/catalog-sort';

/**
 * Предзагрузка данных категории и товаров в кэш SWR при наведении на ссылку.
 * Пишет и обычный ключ страницы, и ключ useSWRInfinite — иначе при переходе
 * infinite всё равно ждал бы сеть.
 */
export function usePrefetchCategory() {
  const { region } = useRegion();

  return useCallback(
    (slug: string) => {
      if (!slug) return;

      const categoryKey = `/api/categories/${slug}`;
      const listParams = {
        category_slug: slug,
        page: 1,
        per_page: CATALOG_PRODUCTS_PER_PAGE,
        sort_by: 'created_at' as const,
        sort_order: 'desc' as const,
        region_id: region?.id,
      };
      const productsKey = getCategoryProductsKey(slug, {
        page: 1,
        per_page: CATALOG_PRODUCTS_PER_PAGE,
        sort_by: 'created_at',
        sort_order: 'desc',
        region_id: region?.id,
      });

      // Тот же getKey, что на странице категории для pageIndex=0
      const infiniteGetKey = (
        pageIndex: number,
        previousPageData: { current_page?: number; last_page?: number; meta?: { current_page?: number; last_page?: number } } | null
      ) => {
        if (pageIndex === 0) return productsKey;
        if (!previousPageData) return productsKey;
        const current = previousPageData.current_page ?? previousPageData.meta?.current_page;
        const last = previousPageData.last_page ?? previousPageData.meta?.last_page;
        if (current != null && last != null && current >= last) return null;
        return getCategoryProductsKey(slug, {
          page: pageIndex + 1,
          per_page: CATALOG_PRODUCTS_PER_PAGE,
          sort_by: 'created_at',
          sort_order: 'desc',
          region_id: region?.id,
        });
      };

      api.categories
        .get(slug)
        .then((data) => {
          if (data?.category) setCategoryFragmentFromCategory(data.category);
          mutate(categoryKey, data, { revalidate: false });
        })
        .catch(() => {});

      api.products
        .list(listParams)
        .then((data) => {
          mutate(productsKey, data, { revalidate: false });
          // useSWRInfinite хранит массив страниц под unstable_serialize(getKey)
          mutate(unstable_serialize(infiniteGetKey), [data], { revalidate: false });
        })
        .catch(() => {});
    },
    [region?.id]
  );
}
